// Realtime change notifications for this tab (ADR 0013): SharedWorker (one socket for all tabs) → per-tab WebSocket
// → long-polling. Every path only triggers the normal authorized pull; the short-polling sync loop stays the safety
// net, so a dead channel delays updates but never loses them.
import { RealtimeSocket, realtimeUrl, type RtServerMsg, type RtStatus } from './socket';
import RealtimeWorker from './sharedWorker?sharedworker';

export interface RealtimeHandlers {
  /** `seq`: the workspace's latest change sequence, when the transport knows it */
  onWorkspace(workspaceId: string, seq?: number): void;
  onInbox(): void;
  /** the server ended the session */
  onAuth?(): void;
}
export type RealtimeMode = 'shared-worker' | 'websocket' | 'long-poll' | 'off';
export interface RealtimeHandle { stop(): void; mode(): RealtimeMode }

const LONG_POLL_RETRY = [2_000, 5_000, 15_000, 30_000, 60_000];

/** Current transport, visible to diagnostics and tests as `<html data-tp-realtime>`. */
const show = (m: RealtimeMode) => { try { document.documentElement.dataset.tpRealtime = m; } catch { /* no DOM */ } };

export function startRealtime(userId: string, workspaces: string[], h: RealtimeHandlers): RealtimeHandle {
  let mode: RealtimeMode = 'off';
  let stopped = false;
  let poll: { stop(): void } | null = null;
  const onMsg = (m: RtServerMsg) => {
    if (m.t === 'ws') h.onWorkspace(m.w, m.s);
    else if (m.t === 'inbox') h.onInbox();
    else if (m.t === 'auth') h.onAuth?.();
  };
  // Sockets that cannot connect hand over to long-polling; a socket that opens again takes back over.
  const onStatus = (s: RtStatus, socketMode: RealtimeMode) => {
    if (stopped) return;
    if (s === 'unavailable' && !poll) { poll = longPoll(workspaces, h); mode = 'long-poll'; show(mode); }
    if (s === 'open') { poll?.stop(); poll = null; mode = socketMode; show(mode); }
  };

  let close: () => void = () => undefined;
  const Shared = (globalThis as { SharedWorker?: typeof SharedWorker }).SharedWorker;
  if (Shared) {
    try {
      const worker: SharedWorker = new RealtimeWorker({ name: 'tp-realtime' });
      worker.port.onmessage = (e: MessageEvent<RtServerMsg | { t: 'status'; s: RtStatus }>) => {
        const m = e.data;
        if (m.t === 'status') onStatus(m.s, 'shared-worker'); else onMsg(m);
      };
      worker.port.start();
      worker.port.postMessage({ t: 'hello', user: userId, w: workspaces });
      const bye = () => worker.port.postMessage({ t: 'bye' });
      window.addEventListener('pagehide', bye);
      close = () => { bye(); window.removeEventListener('pagehide', bye); worker.port.close(); };
      mode = 'shared-worker';
    } catch { /* blocked or unsupported (e.g. some private modes): per-tab socket */ }
  }
  if (mode === 'off') {
    if (typeof WebSocket !== 'undefined') {
      const sock = new RealtimeSocket(realtimeUrl(location.origin), onMsg, s => onStatus(s, 'websocket'));
      sock.subscribe(workspaces);
      sock.start();
      close = () => sock.stop();
      mode = 'websocket';
    } else {
      poll = longPoll(workspaces, h);
      mode = 'long-poll';
    }
  }
  show(mode === 'long-poll' ? mode : 'off'); // sockets report 'open' once connected
  return {
    stop: () => { stopped = true; close(); poll?.stop(); poll = null; mode = 'off'; show(mode); },
    mode: () => mode,
  };
}

/** GET /rt/poll: answered when something is newer than what this tab knows, or after ~25 s with no change. */
function longPoll(workspaces: string[], h: RealtimeHandlers): { stop(): void } {
  let stopped = false, ctl: AbortController | null = null, attempt = 0;
  const seqs: Record<string, number> = {};
  let sig: string | null = null;
  const loop = async (): Promise<void> => {
    while (!stopped) {
      const q = new URLSearchParams({ w: workspaces.join(','), s: workspaces.map(w => String(seqs[w] ?? '')).join(',') });
      if (sig !== null) q.set('i', sig);
      ctl = new AbortController();
      try {
        const r = await fetch('/rt/poll?' + q.toString(), { credentials: 'same-origin', cache: 'no-store', signal: ctl.signal });
        if (r.status === 401) { h.onAuth?.(); return; }
        if (!r.ok) throw new Error('poll ' + r.status);
        const body = await r.json() as { t: string; s: Record<string, number>; i: string };
        attempt = 0;
        const first = sig === null;
        for (const [w, s] of Object.entries(body.s || {})) {
          if (!first && s > (seqs[w] ?? 0)) h.onWorkspace(w, s);
          seqs[w] = s;
        }
        if (!first && body.i !== sig) h.onInbox();
        sig = body.i;
      } catch {
        if (stopped) return;
        await new Promise(res => setTimeout(res, LONG_POLL_RETRY[Math.min(attempt++, LONG_POLL_RETRY.length - 1)]));
      }
    }
  };
  void loop();
  return { stop: () => { stopped = true; ctl?.abort(); } };
}
